import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowUpRight, Check, Send } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { speaker } from '@/data/speaker';

const inviteSchema = z.object({
  name: z.string().min(2, 'Diga o seu nome'),
  email: z.string().email('E-mail inválido'),
  organization: z.string().optional(),
  eventName: z.string().min(2, 'Qual é o evento?'),
  eventDate: z.string().optional(),
  city: z.string().optional(),
  format: z.string().optional(),
  audienceSize: z.string().optional(),
  message: z.string().optional(),
});

type InviteValues = z.infer<typeof inviteSchema>;

/** Resumo do convite para quem preferir continuar a conversa no WhatsApp. */
function whatsappLink(values: InviteValues) {
  const linhas = [
    `Olá, Johnny! Enviei um convite para palestra pelo site.`,
    `Evento: ${values.eventName}`,
    values.eventDate && `Data: ${values.eventDate}`,
    values.city && `Cidade: ${values.city}`,
    values.format && `Formato: ${values.format}`,
    `Quem convida: ${values.name}${values.organization ? ` (${values.organization})` : ''}`,
  ].filter(Boolean);
  return `https://wa.me/${speaker.contact.whatsapp}?text=${encodeURIComponent(linhas.join('\n'))}`;
}

export default function InviteForm() {
  const [sent, setSent] = useState<InviteValues | null>(null);
  const [failed, setFailed] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<InviteValues>({ resolver: zodResolver(inviteSchema) });

  const onSubmit = async (values: InviteValues) => {
    setFailed(false);
    const { error } = await supabase.from('speaking_requests').insert({
      name: values.name,
      email: values.email,
      organization: values.organization || null,
      event_name: values.eventName,
      event_date: values.eventDate || null,
      city: values.city || null,
      format: values.format || null,
      audience_size: values.audienceSize || null,
      message: values.message || null,
      source: 'site/palestras',
    });
    if (error) {
      setFailed(true);
      return;
    }
    setSent(values);
  };

  if (sent) {
    return (
      <div className="speak-form-done" role="status">
        <p className="speak-form-done-icon"><Check size={20} aria-hidden="true" /></p>
        <h3>Convite recebido.</h3>
        <p>Respondo no e-mail {sent.email} com a disponibilidade e uma proposta de recorte para o seu público.</p>
        <a className="pulse-button" href={whatsappLink(sent)} target="_blank" rel="noopener noreferrer">
          Avisar pelo WhatsApp <ArrowUpRight size={19} aria-hidden="true" />
        </a>
      </div>
    );
  }

  return (
    <form className="speak-form" onSubmit={handleSubmit(onSubmit)} noValidate>
      <div className="speak-form-row">
        <label className="speak-field">
          <span>Seu nome *</span>
          <input type="text" autoComplete="name" aria-invalid={!!errors.name} {...register('name')} />
          {errors.name && <em>{errors.name.message}</em>}
        </label>
        <label className="speak-field">
          <span>E-mail *</span>
          <input type="email" autoComplete="email" aria-invalid={!!errors.email} {...register('email')} />
          {errors.email && <em>{errors.email.message}</em>}
        </label>
      </div>
      <div className="speak-form-row">
        <label className="speak-field">
          <span>Organização</span>
          <input type="text" autoComplete="organization" {...register('organization')} />
        </label>
        <label className="speak-field">
          <span>Nome do evento *</span>
          <input type="text" aria-invalid={!!errors.eventName} {...register('eventName')} />
          {errors.eventName && <em>{errors.eventName.message}</em>}
        </label>
      </div>
      <div className="speak-form-row">
        <label className="speak-field">
          <span>Data ou período</span>
          <input type="text" placeholder="Ex.: 12 de março, ou março de 2027" {...register('eventDate')} />
        </label>
        <label className="speak-field">
          <span>Cidade</span>
          <input type="text" {...register('city')} />
        </label>
      </div>
      <div className="speak-form-row">
        <label className="speak-field">
          <span>Formato</span>
          <select defaultValue="" {...register('format')}>
            <option value="">A definir</option>
            {speaker.formats.map((format) => (
              <option key={format.id} value={format.name}>{format.name}</option>
            ))}
          </select>
        </label>
        <label className="speak-field">
          <span>Público estimado</span>
          <input type="text" placeholder="Ex.: 200 pessoas" {...register('audienceSize')} />
        </label>
      </div>
      <label className="speak-field">
        <span>Sobre o evento</span>
        <textarea rows={4} placeholder="Quem estará na plateia e o que o evento precisa deixar para trás." {...register('message')} />
      </label>
      {failed && (
        <p className="speak-form-error" role="alert">
          Não consegui registrar o convite agora. Tente de novo ou fale direto pelo{' '}
          <a href={`https://wa.me/${speaker.contact.whatsapp}`} target="_blank" rel="noopener noreferrer">WhatsApp</a>.
        </p>
      )}
      <button type="submit" className="pulse-button" disabled={isSubmitting}>
        {isSubmitting ? 'Enviando…' : 'Enviar convite'} <Send size={18} aria-hidden="true" />
      </button>
    </form>
  );
}
